import { useCallback, useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import SitesService from '../../services/SitesService';
import { useNotificationsWebSocket } from '../../hooks/useNotificationsWebSocket';

const FALLBACK_POLLING_MS = 120_000;

/**
 * También sirve para las solicitudes B2B (type "b2b"): una proforma que pidió un cliente desde su portal. Mismo criterio:
 * el cartel es por usuario, solo para quien tiene acceso a la sucursal de la proforma y permiso para ver proformas B2B,
 * y se repite hasta que la persona lo confirma o hasta que la proforma deja de estar pendiente (aceptada o rechazada).
 *
 * A diferencia del historial de notificaciones (isRead global, una sola vez para todo el
 * tenant), este cartel usa un endpoint por-usuario: cada sesión con acceso a la sucursal debe
 * confirmar el pedido, y la confirmación de un empleado no se lo oculta a los demás. Además solo
 * lista pedidos cuyo envío sigue en PENDING: si alguien ya lo despachó, deja de pedirse confirmación.
 */
const WebOrderAcknowledgementModal = () => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const [error, setError] = useState('');

  const loadPendingOrders = useCallback(async () => {
    try {
      const response = await SitesService.getPendingOrderAcknowledgements();
      setPendingOrders(Array.isArray(response) ? response : []);
    } catch (requestError) {
      // El polling es un respaldo: un fallo temporal no debe bloquear al operador.
      console.warn('No se pudieron consultar los pedidos web pendientes:', requestError);
    }
  }, []);

  const handleNewNotification = useCallback((notification) => {
    const type = String(notification?.type || '').toLowerCase();
    if (type !== 'order' && type !== 'b2b') return;
    // El backend es la fuente de verdad (filtra por usuario, sucursal y estado del envío):
    // refetch en vez de insertar el payload del WS optimistamente.
    void loadPendingOrders();
  }, [loadPendingOrders]);

  useNotificationsWebSocket(handleNewNotification);

  useEffect(() => {
    void loadPendingOrders();
    const interval = setInterval(loadPendingOrders, FALLBACK_POLLING_MS);
    return () => clearInterval(interval);
  }, [loadPendingOrders]);

  useEffect(() => {
    // Señal disparada por NotificationsDropdown cuando su propio fetch (poll o apertura manual)
    // detecta una notificación de pedido: mostrar el cartel ya, sin esperar a este polling de respaldo.
    const handleNewOrdersDetected = () => { void loadPendingOrders(); };
    window.addEventListener('notifications-new-orders', handleNewOrdersDetected);
    return () => window.removeEventListener('notifications-new-orders', handleNewOrdersDetected);
  }, [loadPendingOrders]);

  const currentOrder = pendingOrders[0];
  const isB2B = String(currentOrder?.type || '').toLowerCase() === 'b2b';

  const acknowledgeCurrentOrder = async () => {
    if (!currentOrder || acknowledgingId != null) return;

    setAcknowledgingId(currentOrder.id);
    setError('');
    try {
      await SitesService.acknowledgeOrderNotification(currentOrder.id);
      setPendingOrders((orders) => orders.filter((order) => order.id !== currentOrder.id));
    } catch (requestError) {
      console.error('No se pudo confirmar el aviso:', requestError);
      setError('No se pudo registrar la confirmación. Intentá nuevamente.');
    } finally {
      setAcknowledgingId(null);
    }
  };

  return (
    <Dialog
      open={Boolean(currentOrder)}
      disableEscapeKeyDown
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      <Box sx={{ height: 7, bgcolor: 'warning.main' }} />
      <DialogTitle sx={{ pt: 3, pb: 1, textAlign: 'center' }}>
        <Box sx={{ width: 64, height: 64, mx: 'auto', mb: 1.5, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'warning.light', color: 'warning.dark' }}>
          {isB2B ? <BusinessRoundedIcon sx={{ fontSize: 36 }} /> : <ReceiptLongRoundedIcon sx={{ fontSize: 36 }} />}
        </Box>
        {isB2B ? 'Nueva solicitud B2B' : 'Nuevo pedido web'}
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', px: { xs: 3, sm: 5 }, pb: 2 }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {isB2B
            ? 'Un cliente B2B hizo una solicitud para tu sucursal. Confirmá que la viste antes de continuar.'
            : 'Hay un pedido nuevo para tu sucursal. Confirmá que lo viste antes de continuar.'}
        </Typography>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.100', textAlign: 'left' }}>
          <Typography sx={{ whiteSpace: 'pre-line', fontWeight: 600 }}>
            {currentOrder?.description || currentOrder?.title}
          </Typography>
        </Box>
        {pendingOrders.length > 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Quedan {pendingOrders.length - 1} aviso{pendingOrders.length - 1 === 1 ? '' : 's'} por confirmar.
          </Typography>
        )}
        {error && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, pt: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={acknowledgeCurrentOrder}
          disabled={acknowledgingId != null}
          startIcon={acknowledgingId != null ? <CircularProgress color="inherit" size={18} /> : <ReceiptLongRoundedIcon />}
          sx={{ py: 1.35, fontWeight: 700 }}
        >
          {isB2B ? 'Entendido' : 'Aceptar pedido'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WebOrderAcknowledgementModal;
